import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    fetchLatestNawDocument,
    fetchNawReadiness,
    generateNawDocument,
    NawLatest,
    NawReadiness,
} from '../../api/workersApi';
import { downloadWorkerFile } from '../../api/workerFilesApi';

/**
 * NAW document section on the worker detail page.
 *
 * Three states drive the UI:
 *   1. No NAW yet  → Generate button (disabled if readiness gate fails).
 *   2. Have NAW    → Download link + Regenerate option.
 *   3. Loading     → spinner.
 *
 * The Generate button's disabled state mirrors the readiness API; an info icon
 * surfaces the Dutch field labels in a tooltip so the recruiter knows what to
 * fill in. We only refresh readiness on mount + after each generation —
 * recruiters who edit fields elsewhere on the page can click the Refresh
 * button next to the info icon.
 */
interface Props {
    workerId: string;
    /** Triggered when a fresh NAW lands so the parent can refresh the files
     *  list / tabs if they're watching for FileType.NAW_DOCUMENT rows. */
    onGenerated?: () => void;
}

export const NawDocumentSection: React.FC<Props> = ({ workerId, onGenerated }) => {
    const [readiness, setReadiness] = useState<NawReadiness | null>(null);
    const [latest, setLatest] = useState<NawLatest | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [generating, setGenerating] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [r, l] = await Promise.all([
                fetchNawReadiness(workerId),
                fetchLatestNawDocument(workerId),
            ]);
            setReadiness(r);
            setLatest(l);
        } catch (err: any) {
            setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load NAW state');
        } finally {
            setLoading(false);
        }
    }, [workerId]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const handleGenerate = async () => {
        setGenerating(true);
        setError(null);
        try {
            await generateNawDocument(workerId);
            // Refresh both readiness + latest after generation. The new NAW
            // lands in worker_files immediately; latest endpoint returns it.
            await loadAll();
            onGenerated?.();
        } catch (err: any) {
            const data = err?.response?.data;
            if (data?.error_code === 'required_fields_missing') {
                setError(
                    `Required fields missing: ${(data.missing ?? []).join(', ')}. ` +
                    'Update the worker profile, then try again.',
                );
                // Refresh readiness so the tooltip is current too.
                try {
                    setReadiness(await fetchNawReadiness(workerId));
                } catch {
                    /* tooltip stays stale; not blocking */
                }
            } else if (data?.error_code === 'not_implemented') {
                setError('VCA-attachment generation is not yet implemented in this version.');
            } else {
                setError(data?.message ?? err?.message ?? 'NAW generation failed');
            }
        } finally {
            setGenerating(false);
        }
    };

    const handleDownload = async () => {
        if (!latest) return;
        try {
            // downloadWorkerFile returns the Blob — we need to wire up the
            // actual browser download ourselves (the API helper doesn't).
            const blob = await downloadWorkerFile(latest.fileId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = latest.fileName || `naw-document-${latest.fileId}.pdf`;
            document.body.appendChild(a);
            a.click();
            // Detach after the click runs in the next tick — Firefox needs the
            // node still attached when the click fires, others don't care.
            setTimeout(() => {
                a.remove();
                window.URL.revokeObjectURL(url);
            }, 0);
        } catch (err: any) {
            setError(err?.response?.data?.message ?? err?.message ?? 'Download failed');
        }
    };

    const renderInfoTooltip = () => {
        if (!readiness || readiness.ready) return null;
        const tooltip = (
            <Box>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                    Missing fields (click to edit):
                </Typography>
                {readiness.missing.map((m) => (
                    <Typography variant="caption" key={m} sx={{ display: 'block' }}>
                        • {m}
                    </Typography>
                ))}
            </Box>
        );
        const jumpToEditor = () => {
            // Scroll to the NawFieldsEditor mounted as id=naw-fields-editor.
            // No router state needed — same tab on the worker detail page.
            const el = document.getElementById('naw-fields-editor');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        };
        return (
            <Tooltip title={tooltip} arrow placement="right">
                <IconButton
                    size="small"
                    color="warning"
                    aria-label="missing fields — click to edit"
                    onClick={jumpToEditor}
                >
                    <InfoOutlinedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        );
    };

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <DescriptionIcon color="primary" />
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    NAW document
                </Typography>
                <Tooltip title="Refresh status">
                    <IconButton size="small" onClick={loadAll} disabled={loading || generating}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>

            {loading ? (
                <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                </Box>
            ) : (
                <Stack spacing={1.5}>
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {latest ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip
                                size="small"
                                color="success"
                                label={`Latest: ${new Date(latest.createdAt).toLocaleDateString('en-GB')}`}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                                {latest.fileName}
                            </Typography>
                            <Button
                                startIcon={<DownloadIcon />}
                                size="small"
                                variant="outlined"
                                onClick={handleDownload}
                            >
                                Download
                            </Button>
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No NAW document generated yet.
                        </Typography>
                    )}

                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Tooltip
                            title={
                                readiness && !readiness.ready
                                    ? 'Fill in the missing fields to generate a NAW'
                                    : ''
                            }
                            arrow
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    onClick={handleGenerate}
                                    disabled={
                                        generating
                                        || !readiness
                                        || !readiness.ready
                                    }
                                    startIcon={generating ? <CircularProgress size={16} /> : undefined}
                                >
                                    {latest ? 'Regenerate' : 'Generate'}
                                </Button>
                            </span>
                        </Tooltip>
                        {renderInfoTooltip()}
                    </Stack>
                </Stack>
            )}
        </Paper>
    );
};
